import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';
import { UserStatusSchema } from '../user-status';

/**
 * The four facts a staff surface needs to **name** an account it is deciding
 * about.
 *
 * It lives in `users` rather than in the module that first needed it, because by
 * now three do: the Trust queue names two accounts per collision, the
 * verification queue names the member whose evidence is being judged, and every
 * later work list — violations, appeals, meetings — names somebody the same way.
 * A per-module copy of this shape is four fields that drift.
 *
 * **Five fields, and the door does not widen.** A reviewer needs to recognise an
 * account (`email`), see where it stands (`status`), read *why* it is where
 * it stands (`statusReason`) — "banned for ban evasion" and "banned for
 * harassment" lead to different verdicts on the same row — and know **which of
 * the two kinds of account** it is (`isStaff`, P27c1): a staff account that
 * reaches a queue as a reporter or an actor must be named as one, or the row
 * reads as a member. Gender, dob, locale and the roles themselves are not part
 * of that judgement, and a door that grows one field per consumer is how "who
 * may read a member's account row" stops having a short answer. A surface that
 * needs more is asking for the members detail, which has its own permission.
 *
 * **Not a profile read.** An email and a lifecycle status are account facts, not
 * question-engine answers: nothing here reaches around the visibility resolver
 * (N18), and nothing here is a member's own content.
 *
 * The api-side counterpart is `UserAccountRef` on `USERS_GET_ACCOUNT_REFS`, the
 * batched QueryBus contract that resolves these across a module boundary (N6) —
 * an id that resolves to nothing is **absent** from that map, which is why every
 * consumer holds this schema as `.nullable()` and draws "no longer available"
 * rather than blanking the row.
 *
 * **There is deliberately no `bannedAt`.** `access.users` records no timestamp
 * for a status change — `updatedAt` moves on any write at all — so a date here
 * would be a fabricated fact on the screens where a fabricated fact bans
 * somebody. (P14b, 2026-08-25: if N9 ever gains a `statusChangedAt`, this is the
 * shape that should carry it.)
 */
export const AccountRefSchema = z
  .object({
    userId: UuidSchema,
    email: z.email(),
    status: UserStatusSchema,
    /** The ban or suspension explanation, when the account has one. */
    statusReason: z.string().nullable(),
    /** Holds a role other than `member` — the one definition of staff, see `UserSchema`. */
    isStaff: z.boolean(),
  })
  .strict();

export type AccountRef = z.infer<typeof AccountRefSchema>;
